import { useState, type ReactNode } from 'react';
import { track } from '@/lib/analytics.js';
import { APP_STORE_URL, PLAY_STORE_URL } from './content.js';

function AppleIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="size-7" fill="currentColor" aria-hidden>
      <path d="M16.37 1.43c0 1.14-.49 2.27-1.18 3.08-.74.9-1.99 1.57-2.99 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.57-2.27 1.21-2.98.8-.94 2.14-1.64 3.25-1.68.03.13.05.28.05.43zm4.56 15.71c-.03.07-.46 1.58-1.52 3.12-.94 1.34-1.94 2.71-3.43 2.71-1.52 0-1.9-.88-3.63-.88-1.7 0-2.3.91-3.67.91-1.38 0-2.33-1.26-3.43-2.8C3.96 18.38 2.93 15.57 2.93 12.92c0-4.28 2.8-6.55 5.55-6.55 1.45 0 2.68.95 3.6.95.87 0 2.22-1.01 3.9-1.01.61 0 2.89.06 4.37 2.19-.13.09-2.38 1.37-2.38 4.19 0 3.26 2.85 4.42 2.96 4.45z" />
    </svg>
  );
}

function PlayIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="size-6" aria-hidden>
      <path d="M3.6 1.8 13 12l-9.4 10.2c-.36-.2-.6-.6-.6-1.1V2.9c0-.5.24-.9.6-1.1z" fill="#00c3ff" />
      <path d="M3.6 1.8c.36-.2.82-.2 1.27.05L16.9 8.7 13 12z" fill="#00e676" />
      <path d="M3.6 22.2c.36.2.82.2 1.27-.05L16.9 15.3 13 12z" fill="#ff3d44" />
      <path d="m16.9 8.7 3.4 1.95c.98.56.98 2.14 0 2.7L16.9 15.3 13 12z" fill="#ffd400" />
    </svg>
  );
}

/**
 * App Store and Google Play badges; Play first on Android. A store without a
 * public listing yet (content.ts) shows "Coming soon" instead of a dead link.
 */
export function StoreButtons({ from, onDark = false }: { from: 'hero' | 'cta'; onDark?: boolean }): ReactNode {
  const [android] = useState(() => /android/i.test(window.navigator.userAgent));
  const buttons = [
    { key: 'app_store', href: APP_STORE_URL, icon: <AppleIcon />, small: 'Download on the', big: 'App Store' },
    { key: 'play_store', href: PLAY_STORE_URL, icon: <PlayIcon />, small: 'Get it on', big: 'Google Play' },
  ];
  if (android) buttons.reverse();

  const base = `flex h-14 w-[172px] items-center gap-3 rounded-xl px-4 text-left shadow-md ring-1 transition`;
  return (
    <div className="flex flex-wrap justify-center gap-3">
      {buttons.map((b) =>
        b.href ? (
          <a
            key={b.key}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('landing_cta_click', { cta_id: `${b.key}_${from}` })}
            className={`${base} bg-ink text-white ring-black/10 hover:bg-black active:scale-[0.97]`}
          >
            {b.icon}
            <span className="flex flex-col">
              <span className="text-[11px] leading-tight text-white/80">{b.small}</span>
              <span className="text-lg font-semibold leading-tight">{b.big}</span>
            </span>
          </a>
        ) : (
          <div
            key={b.key}
            aria-label={`${b.big}, coming soon`}
            className={`${base} cursor-default ${onDark ? 'bg-white/15 text-white ring-white/25' : 'bg-card text-ink-soft ring-line'}`}
          >
            {b.icon}
            <span className="flex flex-col">
              <span className={`text-[11px] leading-tight ${onDark ? 'text-white/75' : 'text-muted'}`}>Coming soon to</span>
              <span className="text-lg font-semibold leading-tight">{b.big}</span>
            </span>
          </div>
        ),
      )}
    </div>
  );
}
